"use client";

// Live photo-realistic preview for /customize -- up to 4 real product photos, each pre-aligned by an
// admin via /admin/products/calibration (components/admin/calibration-tool.tsx), re-rendered here with
// whatever colors the shopper currently has selected. Desktop shows every image at once as a flush,
// gapless collage of uniform squares (2026-08-29 -- these are candid action shots, not studio product
// photos, so a plain grid with each tile at its own real aspect ratio came out ragged); mobile shows the
// same set as a swipeable carousel with page-dots, each tile at its own real aspect ratio (no crop --
// there's no flush-collage need there). Tap/click a desktop tile to see it larger, uncropped, in the
// lightbox. The actual 3D rendering (Model, lighting, projection) is the same shared code the admin tool
// uses, from components/three/basalt-mount-model.tsx.
//
// The photo and the live WebGL render are composited onto one visible 2D canvas per tile (see
// compositeToOutput below) rather than shown as two stacked, both-visible layers -- so a plain right-click
// "Save image as" on the grid tile saves the real blended, cover-cropped result directly (browsers save a
// canvas's own pixel buffer directly, whatever was last drawn into it). The square crop uses whatever focal
// point/zoom the admin set for this photo (CollageCropConfig, see basalt-mount-model.tsx) -- an EARLIER
// version tried auto-deriving a crop focal point from the calibrated model position, which broke on real
// photos (a projection landing near/past the calibration camera's own frustum edge drew the whole composite
// completely outside the canvas -- confirmed directly, several real tiles came back blank); admin-set by eye
// is both simpler and can't produce that failure mode (focalCoverFit clamps regardless). The lightbox does
// its own separate, on-demand composite (see LightboxMirror) rather than reusing the (cropped, and
// resolution-capped-for-a-small-tile) grid canvas -- sized to its own actual, much bigger on-screen box, so
// it stays sharp regardless of screen size instead of being upscaled from a small tile's resolution.
//
// The earlier "share one canvas between tile and lightbox" approach was tried once before and reverted
// after appearing to break the whole preview on real iPhone Safari -- but the actual root cause turned out
// to be unrelated: PreviewTile's own box collapsed to zero height from a real WebKit bug (CSS aspect-ratio
// inside a flex-basis:100% item, fixed below via the older height:0+padding-bottom:% technique instead),
// which made EVERYTHING inside the tile disappear regardless of how the canvas itself was built.

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { Bounds, OrbitControls } from "@react-three/drei";
import { DeviceCapabilityDebug } from "./device-capability-debug";
import { primeConcurrencyCapability, useConcurrencyCapable } from "@/lib/webgl-capability";
import {
  Model, SceneLighting, KeyLight, type GroupKey, type Placement, type KeyLightConfig, type AmbientLightConfig,
  type DepthFadeConfig, type CollageCropConfig, type BlendConfig, TRANSPARENT_CANVAS_GL, PLACEMENT_CAMERA,
  focalCoverFit, drawModelBlended,
} from "@/components/three/basalt-mount-model";

export type CalibrationImage =
  Placement & KeyLightConfig & AmbientLightConfig & DepthFadeConfig & CollageCropConfig & BlendConfig
  & { id: string; photoPath: string; displayOrder: number; isMobileDisplay: boolean };

// The calibration tool needs the photo's real aspect ratio before it can size its canvas to match --
// same probe used by the admin page, duplicated locally rather than shared since it's five lines and
// pulling in a whole extra shared-hooks module for one function felt like more indirection than it's worth.
function useImageAspect(photoPath: string): number | null {
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting for a new photoPath
    setAspect(null);
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => { if (!cancelled) setAspect(img.naturalWidth / img.naturalHeight); };
    img.src = photoPath;
    return () => { cancelled = true; };
  }, [photoPath]);
  return aspect;
}

// Composites the (hidden) photo <img> and the (hidden) live WebGL render onto the one visible collage tile
// canvas -- cover-cropped using this photo's own saved CollageCropConfig (focalCoverFit, see
// basalt-mount-model.tsx) to whatever shape the tile is actually displayed at (a uniform square in the
// desktop collage, the photo's own real aspect again on the mobile carousel, see PreviewTile -- where
// focalCoverFit is a no-op crop since destAspect already equals photoAspect there). This is what makes a
// plain right-click "Save image as" on the grid tile produce the real blended, cropped-to-match result:
// browsers save a canvas's own pixel buffer directly, whatever was last drawn into it. Sized to the
// on-screen DISPLAY resolution (× devicePixelRatio for sharpness), not the photo's full native pixel
// resolution -- rendering the WebGL layer continuously at full native photo resolution for every visible
// tile would cost meaningfully more GPU time than rendering at the modest on-screen size these tiles
// actually display at, and a smaller saved image was an accepted tradeoff for that. The lightbox does NOT
// read from this (cropped) canvas -- see LightboxMirror, which composites its own, full/uncropped view
// fresh, sized to its own actual (much bigger) display box, rather than sharing a resolution budget with
// the small tile.
// Cheap "has the model actually painted yet" check -- downsample the GL canvas to a tiny buffer and
// count non-transparent pixels. Blank/first-frame canvas ~0; a rendered model is well above the floor.
function glCanvasHasModel(glCanvas: HTMLCanvasElement): boolean {
  const s = 48;
  const c = document.createElement("canvas");
  c.width = s; c.height = s;
  const ctx = c.getContext("2d");
  if (!ctx) return true; // can't tell -- assume yes rather than block the tile forever
  ctx.drawImage(glCanvas, 0, 0, s, s);
  const d = ctx.getImageData(0, 0, s, s).data;
  let opaque = 0;
  for (let i = 3; i < d.length; i += 4) if (d[i] > 20) opaque++;
  return opaque > 12;
}

// Returns whether it produced a FINISHED composite (photo + a rendered model), so the caller can hold a
// loading mask over the tile until then. It still draws in the not-yet-ready cases so the retry keeps
// the output canvas current -- the return value only gates the reveal.
function compositeToOutput(container: HTMLElement, image: CollageCropConfig & BlendConfig): boolean {
  const img = container.querySelector<HTMLImageElement>(".customize-preview-tile-photo");
  // R3F's <Canvas className=...> lands on a wrapping <div> it renders internally, not the real <canvas>
  // element -- that real canvas is a descendant, hence the nested selector.
  const glCanvas = container.querySelector<HTMLCanvasElement>(".customize-preview-tile-gl canvas");
  const outCanvas = container.querySelector<HTMLCanvasElement>(".customize-preview-tile-output");
  if (!img || !glCanvas || !outCanvas || !img.complete || img.naturalWidth === 0) return false;
  const rect = container.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  const dpr = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.round(rect.width * dpr));
  const height = Math.max(1, Math.round(rect.height * dpr));
  if (outCanvas.width !== width) outCanvas.width = width;
  if (outCanvas.height !== height) outCanvas.height = height;
  const ctx = outCanvas.getContext("2d");
  if (!ctx) return false;
  ctx.clearRect(0, 0, width, height);
  const photoAspect = img.naturalWidth / img.naturalHeight;
  const { drawW, drawH, offsetX, offsetY } = focalCoverFit(photoAspect, width, height, image.collageFocalX, image.collageFocalY, image.collageZoom);
  ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
  // Destination box is the WHOLE output canvas (0,0,width,height) -- offsetX/offsetY/drawW/drawH is the
  // photo's own cover-fit crop transform, passed separately as modelCrop so glCanvas (which renders at the
  // photo's real aspect, not this tile's) gets drawn with the SAME crop instead of stretched to fill the
  // tile -- see drawModelBlended's own comment for the bug this fixes (confirmed directly: without this,
  // the model's own silhouette read visibly distorted on any tile whose crop isn't the photo's real aspect,
  // i.e. every square collage tile, desktop or mobile).
  drawModelBlended(ctx, glCanvas, 0, 0, width, height, image, { x: offsetX, y: offsetY, w: drawW, h: drawH });
  return glCanvasHasModel(glCanvas);
}

// Retries `fn` on every animation frame until `deadlineMs` of real wall-clock time has passed since the
// call, not a fixed frame count -- a first, frame-counted version (20 frames, ~1/3 second at 60fps)
// looked reasonable against desktop testing but gave up too early on a real iPhone: shader compile + a
// WebGL canvas's genuinely first frame took longer than that window there, so the tile's output canvas
// got stuck on an early "photo only, no model yet" composite and nothing ever re-tried it afterward
// (confirmed directly: the tile stayed wrong indefinitely, while a *later* lightbox open -- a fresh
// mirror-copy attempt, by which point the underlying WebGL canvas had actually finished -- showed the
// model correctly). Wall-clock time is robust to however slow that cold start actually is on a given
// device, where a frame count silently assumes a frame rate that a struggling first paint won't be
// hitting anyway.
function retryUntilDeadline(fn: () => void, deadlineMs: number): () => void {
  let rafId = 0;
  const start = performance.now();
  const tick = () => {
    fn();
    if (performance.now() - start < deadlineMs) rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(rafId);
}

const COMPOSITE_RETRY_MS = 4000;

// The hidden WebGL canvas's OWN render resolution -- deliberately much bigger than any collage tile ever
// displays at, and fixed regardless of it. Previously this canvas was styled 100%/100% of its (small)
// collage-tile container, so its actual backing buffer was only ever as sharp as the tiny tile -- fine for
// the tile itself, but the lightbox (LightboxMirror) draws from this SAME canvas into a box up to 88vh,
// which meant upscaling that tiny buffer and showing visibly blurry model geometry there (confirmed: the
// photo layer stayed sharp since <img> keeps its own native resolution regardless of CSS box size --  only
// the GL layer degraded). Rendering this source canvas at a fixed, generous resolution up front means both
// the small tile draw (downscaled, always looks fine) and the big lightbox draw (now equal-or-bigger than
// most on-screen sizes, no upscaling needed) come from the same adequately-sharp source.
const HIDDEN_GL_RENDER_PX = 1600;
// Desktop-only. First tried unconditionally (mobile included, sized down to 500px rather than the full
// 1600px) but 500px is STILL wider than a real iPhone viewport (390-430px) and that was enough on its own:
// a fixed-size absolutely-positioned element wider than the viewport made the WHOLE PAGE horizontally
// draggable on real iOS Safari, exposing blank space -- confirmed directly from a real iPhone screenshot.
// It's clipped visually by .customize-preview-tile's own overflow:hidden (nothing was ever actually shown
// outside the tile), but WebKit's rubber-band/elastic-scroll range reacts to the underlying layout geometry
// regardless of that clipping, which this emulated environment's own overflow checks never reproduced.
// Mobile doesn't need ANY inflation past the tile's own box to begin with -- this fixed-size trick exists
// only to decouple the hidden canvas's resolution from a DESKTOP collage tile that's much smaller than the
// big lightbox draws from it (see HIDDEN_GL_RENDER_PX above); mobile has no lightbox at all (see PreviewTile
// -- popup disabled there) and shows a single tile already at full viewport width, so the tile's own natural
// 100% sizing (same as before that fix existed) was already exactly the resolution that scenario needs, with
// zero risk of ever exceeding the viewport.
// Measures REAL, steady-state frame timing off the primary tile's own render loop -- the actual GLB/
// shader/lighting it already renders, not a synthetic probe scene -- for ~1s once that tile's first real
// frame has already landed (armed=ready, wired below -- NOT from mount, which would count the one-time
// shader-compile stall as if it were ongoing per-frame cost).
//
// Comparison-only now -- see CustomizePreviewGallery's fpsMeasurement state and DeviceCapabilityDebug.
// This was the ORIGINAL replacement for navigator.hardwareConcurrency/deviceMemory (directly falsified
// by a real device: an 8-core budget Android tablet measured worse at rendering this exact scene than a
// 4-core iPhone, because CPU core count doesn't track GPU rendering power the way it might seem to --
// Apple prioritizes per-core/GPU strength over raw core count, budget Android SoCs often spend their die
// area the opposite way), and it's still the most direct measurement of the three approaches tried. But
// it was in turn replaced as the thing actually GATING concurrency (see checkConcurrencyCapableByMaxTextureSize,
// lib/webgl-capability.ts) purely for speed -- ~1-2 real seconds here vs single-digit milliseconds there,
// which means every tile's live/static decision can now be made essentially immediately instead of the
// other tiles visibly upgrading a couple seconds after the page settles. Still run for its own sake: it
// measures the actual bottleneck directly instead of
// inferring it from a spec number that turned out to point the wrong way on real hardware.
const FPS_SAMPLE_MS = 1000;
const FPS_CAPABLE_THRESHOLD = 45;

function FpsProbe({
  armed, onMeasured,
}: { armed: boolean; onMeasured?: (result: { fps: number; capable: boolean; decisionMs: number }) => void }) {
  const startRef = useRef<number | null>(null);
  const frameCountRef = useRef(0);
  const doneRef = useRef(false);

  useFrame(() => {
    if (!armed || doneRef.current) return;
    const now = performance.now();
    if (startRef.current === null) {
      startRef.current = now; // first armed frame is the window's start, not a sample itself
      frameCountRef.current = 0;
      return;
    }
    frameCountRef.current += 1;
    const elapsed = now - startRef.current;
    if (elapsed >= FPS_SAMPLE_MS) {
      doneRef.current = true;
      const fps = (frameCountRef.current * 1000) / elapsed;
      onMeasured?.({ fps, capable: fps >= FPS_CAPABLE_THRESHOLD, decisionMs: elapsed });
    }
  });

  return null;
}

// R3F's own <Canvas> disposal on unmount does NOT reliably release the underlying GPU-level WebGL
// context -- confirmed directly (a real reported iPad crash, repeatedly navigating to /customize, back,
// revisiting): after unmounting a tile, its WebGLRenderingContext still reported isContextLost()===false,
// left for JS garbage collection to eventually reclaim rather than explicitly released on unmount. GC
// timing is non-deterministic and, on a memory-constrained platform, can't keep pace with repeated
// navigation cycles -- each fresh visit creates new contexts faster than GC reclaims the old ones,
// eventually exceeding the platform's hard per-page WebGL context ceiling and crashing the tab.
//
// Grabs the real <canvas> element via a plain ref callback (Canvas's own `ref` prop, NOT `onCreated`) --
// `onCreated` was tried first and turned out to only fire after R3F's render loop has actually painted a
// frame via requestAnimationFrame, which doesn't help here for two reasons: it couldn't be verified at
// all in this session's own Browser pane (a pre-existing, established limitation -- rAF never fires
// there since the pane isn't genuinely composited), and more importantly, a tile unmounted quickly enough
// during rapid navigation cycling (exactly the crash scenario) might never get a first painted frame at
// all, meaning `onCreated` would never fire for it and this fix would silently do nothing for the exact
// case it exists to cover. A canvas's WebGL context is created synchronously when the WebGLRenderer is
// constructed, well before any frame is painted, and calling `canvas.getContext(type)` again always
// returns that SAME existing context (a canvas can only ever have one) -- so grabbing it directly off the
// element via ref sidesteps the render-loop dependency entirely.
//
// Uses React 19's ref-callback-returns-its-own-cleanup form (paired setup/teardown scoped to ONE
// specific DOM node), NOT a separate useRef + useEffect pair -- tried that first and it broke: the two
// hooks fire independently, and confirmed directly that React nulls the ref (calling this callback with
// null) BEFORE a separate useEffect's own cleanup runs, so the effect always found the ref already
// cleared. Pairing cleanup directly in the ref callback's closure fixes that ordering problem, but
// introduces a second one dev Strict Mode's simulated mount->unmount->remount double-invoke ALSO applies
// to ref-callback cleanup -- and unlike normal React state, a lost WebGL context can't be "undone" by a
// simulated remount: R3F never recreates the real canvas for it (the DOM node was never truly
// destroyed), so the very first Strict-Mode-simulated detach permanently killed the one real context
// before the component had even properly rendered once (confirmed directly: isContextLost() was already
// true before any real navigation ever happened). The fix is the isConnected check below: Strict Mode's
// simulated detach calls this cleanup WITHOUT ever actually removing the node from the document (it's
// the same canvas, kept and immediately re-attached), so isConnected still reads true there -- only a
// REAL removal ever makes it false. That check has to be DEFERRED one macrotask, not read synchronously
// in the cleanup itself, because React invokes ref-cleanup before physically completing the DOM removal
// -- confirmed directly, even a genuine unmount's canvas.isConnected still read true at that exact
// synchronous moment. A plain setTimeout(0), not requestAnimationFrame -- rAF isn't reliable to depend on
// here (confirmed elsewhere this session: it doesn't fire at all in some non-composited contexts),
// whereas a macrotask always runs regardless.
function useDisposeGLOnUnmount() {
  return useCallback((canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    return () => {
      window.setTimeout(() => {
        if (canvas.isConnected) return; // still attached -- Strict Mode's simulated detach, not a real removal
        const gl = (canvas.getContext("webgl") ?? canvas.getContext("webgl2") ?? canvas.getContext("experimental-webgl")) as
          | WebGLRenderingContext
          | WebGL2RenderingContext
          | null;
        gl?.getExtension("WEBGL_lose_context")?.loseContext();
      }, 0);
    };
  }, []);
}

function PreviewTileContent({
  image, groupColors, photoAspect, measurePerformance, onPerformanceMeasured, singleTileLayout, modelPath,
}: {
  image: CalibrationImage; groupColors: Record<GroupKey, string>; photoAspect: number; modelPath: string;
  // Only the primary tile ever gets these -- comparison data for DeviceCapabilityDebug now (see
  // CustomizePreviewGallery's fpsMeasurement state), not what actually gates the other tiles anymore.
  measurePerformance?: boolean;
  onPerformanceMeasured?: (result: { fps: number; capable: boolean; decisionMs: number }) => void;
  // See PreviewTile's own comment -- true on real mobile OR a wide-but-capability-constrained device.
  // Drives the hidden canvas's OWN render resolution below: the actual GPU fragment-shading cost scales
  // with pixel count, so this (not just how many WebGL contexts are concurrently live) is what a weak
  // device's real slowness was coming from -- rendering at the old fixed 1600px desktop size regardless
  // of how small the tile it fed actually displayed at.
  singleTileLayout?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const glCanvasRef = useDisposeGLOnUnmount();

  // The tile stays under an opaque loading mask until its first FINISHED composite -- so the shopper
  // never sees the flash of (a) the raw calibration photo (which still shows the real prototype) or
  // (b) the model rendering in the GLB's default grey before its per-part colours are applied. Reset
  // only when the photo itself changes; a colour change re-composites but the tile is already revealed
  // (re-masking would flash on every click).
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);
  const colorsAppliedRef = useRef(false);
  const streakRef = useRef(0);
  const markReady = useCallback(() => {
    if (!readyRef.current) { readyRef.current = true; setReady(true); }
  }, []);
  const handleColorsApplied = useCallback(() => { colorsAppliedRef.current = true; }, []);

  useEffect(() => {
    readyRef.current = false; colorsAppliedRef.current = false; streakRef.current = 0;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-mask for a new photo, same tradeoff as useImageAspect
    setReady(false);
  }, [image.id]);

  // Re-composites whenever the color selection changes, plus once on mount -- NOT continuously forever,
  // since nothing here animates on its own; see retryUntilDeadline above for why this is time-bounded
  // rather than frame-counted. Reveals the tile once compositeToOutput reports a finished frame (photo +
  // a painted model) AND the colours have landed, held for 2 frames; a hard fallback timer reveals it
  // regardless shortly after the retry window so a too-strict check can't leave a permanent spinner.
  useEffect(() => {
    const stop = retryUntilDeadline(() => {
      const container = containerRef.current;
      if (!container) return;
      const finished = compositeToOutput(container, image);
      if (readyRef.current) return;
      if (finished && colorsAppliedRef.current) {
        if (++streakRef.current >= 2) markReady();
      } else {
        streakRef.current = 0;
      }
    }, COMPOSITE_RETRY_MS);
    const fallback = window.setTimeout(markReady, COMPOSITE_RETRY_MS + 500);
    return () => { stop(); window.clearTimeout(fallback); };
  }, [groupColors, image, markReady]);

  // Re-composites on resize too (grid<->carousel breakpoint, lightbox open/close, window resize) -- the
  // output canvas's own pixel buffer needs to be resized AND redrawn, not just stretched via CSS, or it'd
  // look soft/blurry after the box changes size.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => compositeToOutput(container, image));
    observer.observe(container);
    return () => observer.disconnect();
  }, [image]);

  return (
    <div
      ref={containerRef}
      data-image-id={image.id}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      {/* Full desktop grid: fixed pixel size (HIDDEN_GL_RENDER_PX), NOT a percentage of the outer tile --
          gives the Canvas below its own high, tile-size-independent render resolution;
          .customize-preview-tile-gl's width/height:100% (globals.css) then resolves against THIS div's
          fixed size rather than the small on-screen collage tile. Mobile OR singleTileLayout: plain
          100%/height-from-aspect, same as every other box in this file -- deliberately NOT inflated past
          the tile's own real size (see HIDDEN_GL_RENDER_PX's comment for why that's safe to skip there).
          singleTileLayout matters here MORE than anywhere else it's threaded through: this is the actual
          WebGL render resolution, and GPU fragment-shading cost scales with pixel count -- a
          capability-constrained device rendering at the old fixed 1600px regardless of its own tile size
          was real, avoidable extra GPU work on exactly the hardware least able to afford it. */}
      <div
        style={singleTileLayout
          ? { position: "absolute", top: 0, left: 0, width: "100%", height: 0, paddingBottom: `${(1 / photoAspect) * 100}%` }
          : { position: "absolute", top: 0, left: 0, width: HIDDEN_GL_RENDER_PX, height: HIDDEN_GL_RENDER_PX / photoAspect }}
      >
        {/* Photo and WebGL render are both present but invisible -- never display:none (would stop the
            canvas rendering/tracking size), and deliberately opacity:0.01, not exactly 0, as a defensive
            measure against a *suspected* (never independently confirmed) Safari quirk around exactly-zero-
            opacity canvases. They exist purely as source pixels for compositeToOutput below AND for
            LightboxMirror, which reads these same elements directly when enlarged rather than sharing a
            canvas with the small collage tile -- the one thing actually shown here is -tile-output below,
            fully opaque once composited and painting over this box entirely regardless. */}
        {/* eslint-disable-next-line @next/next/no-img-element -- admin-supplied local path, not user content */}
        <img
          src={image.photoPath} alt="" className="customize-preview-tile-photo"
          style={{ opacity: 0.01 }}
          onLoad={() => { if (containerRef.current) compositeToOutput(containerRef.current, image); }}
        />
        {/* frameloop="demand" was tried here (render once per prop change instead of continuously, to help
            offset HIDDEN_GL_RENDER_PX's bigger buffer) and reverted -- it relies on R3F automatically
            invalidating whenever groupColors/placement props change, and a missed or delayed invalidation
            (e.g. several rapid color changes landing in one React commit, or this bigger canvas's first
            frame just being slow) left a tile permanently showing a stale, uncolored GL frame: nothing
            forces the GL layer itself to retry once compositeToOutput's own retry window gives up, unlike
            the default continuous loop, which self-corrects within one frame if a render is ever late.
            Confirmed directly (a real collage tile came back with no color applied). Default frameloop
            costs more continuous GPU time across up to 4 concurrent desktop tiles, but always converges
            on the right frame, which matters more than the saving. */}
        <Canvas
          gl={TRANSPARENT_CANVAS_GL} className="customize-preview-tile-gl" camera={PLACEMENT_CAMERA}
          style={{ opacity: 0.01 }} ref={glCanvasRef}
        >
          <SceneLighting exposure={image.exposure} envIntensity={image.envIntensity} fillIntensity={image.fillIntensity} />
          <KeyLight config={image} targetPosition={[image.posX, image.posY, image.posZ]} />
          <group
            position={[image.posX, image.posY, image.posZ]}
            rotation={[(image.rotXDeg * Math.PI) / 180, (image.rotYDeg * Math.PI) / 180, (image.rotZDeg * Math.PI) / 180]}
            scale={image.scale}
          >
            <Model
              groupColors={groupColors} depthFade={image} pivot={[image.posX, image.posY, image.posZ]}
              onColorsApplied={handleColorsApplied} modelPath={modelPath}
            />
          </group>
          {measurePerformance && <FpsProbe armed={ready} onMeasured={onPerformanceMeasured} />}
        </Canvas>
      </div>
      {/* The visible collage tile -- cover-cropped by compositeToOutput to whatever shape it's actually
          displayed at. */}
      <canvas className="customize-preview-tile-output" />
      {/* Opaque mask over the tile until its first finished composite (see the `ready` logic above) --
          hides the raw-photo and grey-model flashes. Stays mounted and fades via a class so it can
          transition out. */}
      <div className={`customize-preview-tile-loading${ready ? " is-ready" : ""}`} aria-hidden="true" />
    </div>
  );
}

// The lightbox composites fresh, reading directly from the SAME hidden <img>/<Canvas> elements the grid
// tile keeps warm behind it (see PreviewTileContent) -- rather than either mounting a genuinely new,
// independent WebGL context (visibly slower first paint, confirmed directly) or copying the small collage
// tile's own canvas (that tile is cover-cropped to a uniform square now -- see compositeToOutput/
// PreviewTile -- and capping this at a fixed resolution independent of the actual lightbox size looked
// soft on any screen where the lightbox displayed bigger than that cap, confirmed directly against a real
// photo). Sizing THIS canvas's own OUTPUT to its actual on-screen box (up to 88vh, meaningfully bigger than
// a small collage tile) is necessary but wasn't sufficient on its own -- the source glCanvas it draws from
// used to be capped at the tiny collage tile's own resolution (still true of the photo <img> layer, which
// stays sharp regardless since it's an <img> with its own native resolution, but the GL layer visibly
// blurred once stretched past that cap). See HIDDEN_GL_RENDER_PX above PreviewTileContent, which gives the
// source canvas its own resolution independent of the small tile so this draw is a downscale/near-1:1, not
// an upscale, on typical screens.
function LightboxMirror({
  imageId, groupColors, blend,
}: { imageId: string; groupColors: Record<GroupKey, string>; blend: BlendConfig }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const composite = () => {
    const canvas = canvasRef.current;
    const img = document.querySelector<HTMLImageElement>(`[data-image-id="${imageId}"] .customize-preview-tile-photo`);
    const glCanvas = document.querySelector<HTMLCanvasElement>(`[data-image-id="${imageId}"] .customize-preview-tile-gl canvas`);
    if (!canvas || !img || !glCanvas || !img.complete || img.naturalWidth === 0) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    drawModelBlended(ctx, glCanvas, 0, 0, width, height, blend);
  };

  // On open, the source tile's own WebGL frame may need a moment (same wall-clock-bounded reasoning as
  // PreviewTileContent's own retry loop -- a real device's first frame can be genuinely slow), and re-runs
  // whenever the shopper changes color while the lightbox is already open.
  useEffect(() => (
    retryUntilDeadline(composite, COMPOSITE_RETRY_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- imageId is stable per mount, groupColors is the real trigger
  ), [groupColors, imageId]);

  // Redraws on resize too (window resize while open) -- this canvas's own pixel buffer needs to be resized
  // AND redrawn to stay sharp, not just stretched via CSS.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(composite);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  return <canvas ref={canvasRef} className="customize-preview-lightbox-canvas" />;
}

// Matches the CSS carousel breakpoint below (860px) -- the lightbox popup is disabled below it (see
// PreviewTile's onClick), since the popup has been the fragile surface on real mobile Safari across
// several rounds of this. Tracked via matchMedia rather than a fixed layout assumption so it responds to
// an actual resize/orientation change, not just the width at first mount.
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

function PreviewTile({
  image, groupColors, onExpand, squareGrid, isActive, suspendLive, isPrimary, concurrencyCapable, onPerformanceMeasured, singleTileLayout, modelPath,
}: {
  image: CalibrationImage; groupColors: Record<GroupKey, string>; onExpand: () => void; modelPath: string;
  squareGrid: boolean; isActive: boolean;
  // True on real mobile OR a wide-but-capability-constrained device (CustomizePreviewGallery's own
  // singleTileLayout) -- explicit correction: the constrained case must fully adopt mobile's own
  // settings (compact square crop, compact render resolution, no lightbox), not a separate "still
  // full-size" treatment. That's what was actually causing the reported slowness -- the render size
  // itself, not (just) how many contexts were live. See inlinePercent/onClick/aria-label and
  // PreviewTileContent's own hidden-canvas sizing below, all keyed off THIS now, not the raw isMobile
  // viewport check.
  singleTileLayout?: boolean;
  // True while the free-orbit 3D lightbox (Preview3DLightbox, below) is open AND we're on mobile --
  // unmounts this tile's own WebGL context for as long as that's up, so the two are never alive at once.
  // Mobile Safari has a real, confirmed history of crashing from concurrent/rapidly-churned WebGL
  // contexts (see mountLive's own comment below); the 3D lightbox is exactly that risk on top of the one
  // context this tile already keeps warm there. Desktop never had that crash history to begin with (it
  // already runs every tile's context concurrently, always, per mountLive below) -- suspending there too
  // would only add an unnecessary reload flash with no matching safety benefit, so the caller passes
  // `false` on desktop regardless of whether the lightbox is open.
  suspendLive?: boolean;
  // True for exactly one tile (index 0, see CustomizePreviewGallery) on desktop -- that tile always
  // mounts live regardless of concurrencyCapable, both to have SOMETHING to show and because it's the
  // one that runs FpsProbe (comparison data only now, see mountLive's own comment below).
  isPrimary?: boolean;
  // null until CustomizePreviewGallery's one-time checkConcurrencyCapableByMaxTextureSize check resolves,
  // then whether this device has headroom for more concurrent WebGL contexts. Only meaningful on desktop
  // -- see mountLive below.
  concurrencyCapable?: boolean | null;
  onPerformanceMeasured?: (result: { fps: number; capable: boolean; decisionMs: number }) => void;
}) {
  const aspect = useImageAspect(image.photoPath);
  const isMobile = useIsMobile();
  // Desktop collage (squareGrid, multiple photos): CSS `aspect-ratio:1` on .customize-preview-tile (see
  // globals.css) drives a uniform, gapless grid -- no inline override needed. Desktop single-photo case
  // (nothing to align flush against, so no reason to crop it) falls back to the real photo aspect via
  // height:0+padding-bottom instead. Mobile is ALWAYS this same inline height:0+padding-bottom technique,
  // regardless of squareGrid's value -- never the bare CSS aspect-ratio:1 rule, square or not, even though
  // "square" is what that rule already says: WebKit has a real, confirmed bug where aspect-ratio inside a
  // flex item with a flex-basis can resolve to zero height and collapse the tile invisibly, independent of
  // which ratio it's set to (confirmed directly on a real iPhone -- this was the root cause of an apparent
  // "whole preview missing" bug once before). inlinePercent below is null only for the one case that's
  // genuinely safe to leave to the CSS rule: desktop's own squareGrid tiles. singleTileLayout, not just
  // isMobile -- a wide-but-capability-constrained device needs the exact same compact square treatment.
  const inlinePercent = singleTileLayout ? 100 : (!squareGrid && aspect ? (1 / aspect) * 100 : null);
  // Live 3D restored on mobile, at the shopper's own explicit request after a first attempt at this
  // crashed real iPhone Safari (per-tile WebGL context, only the active carousel slide mounted, still
  // crashed) and a follow-up "no WebGL on mobile at all" version was confirmed safe but lost the live
  // color preview there entirely. This is still the same "only the active slide is live" approach, but
  // `isActive` here is now DEBOUNCED (see CustomizePreviewGallery's activeIndex vs its own
  // debouncedActiveIndex) rather than tracking scroll position instantly -- the working theory is that
  // rapid WebGL context creation/destruction during a fast multi-swipe, not steady-state context count, is
  // what actually crashed it (a known WebKit weak spot); debouncing means a quick flick through several
  // photos only ever mounts the ONE context the swipe actually settles on, not one per photo passed
  // through. Still can't be verified from here (only an emulated mobile viewport, not real Safari) -- this
  // is a real device test, not a guaranteed fix.
  //
  // Desktop USED to just keep every tile live always -- reverted after a real device (a wide-viewport but
  // GPU-weak Android tablet) showed that's not actually safe to assume from viewport width. Now only the
  // primary tile mounts unconditionally; the rest wait for concurrencyCapable, a one-time
  // checkConcurrencyCapableByMaxTextureSize check (lib/webgl-capability.ts) resolved in
  // CustomizePreviewGallery. Chosen over the real FpsProbe measurement specifically for speed (single-
  // digit ms vs ~1-2 real seconds) -- see that check's own comment for the full reasoning, and
  // FpsProbe's for why static device-spec proxies were tried and abandoned before either of these.
  const mountLive = (isMobile ? isActive : (isPrimary || concurrencyCapable === true)) && !suspendLive;
  return (
    <button
      type="button"
      className="customize-preview-tile"
      // Popup disabled on mobile (and now the capability-constrained case too) -- undefined onClick makes
      // this a plain, non-interactive display tile there rather than a dead button that looks clickable.
      onClick={singleTileLayout ? undefined : onExpand}
      // Padding-bottom is a pre-2020 technique every browser, including real WebKit, has always handled
      // correctly -- see inlinePercent's own comment above for why this specific box can't just trust the
      // plain CSS aspect-ratio:1 rule the way most of this codebase does.
      style={inlinePercent !== null ? { height: 0, paddingBottom: `${inlinePercent}%`, cursor: singleTileLayout ? "default" : "pointer" } : undefined}
      aria-label={singleTileLayout ? undefined : "View this angle larger"}
    >
      {aspect && (mountLive ? (
        <PreviewTileContent
          image={image} groupColors={groupColors} photoAspect={aspect} singleTileLayout={singleTileLayout} modelPath={modelPath}
          // Runs on mobile too, not just desktop -- the RESULT doesn't gate anything there today (mobile
          // never shows more than one tile regardless), but it's real, useful comparison data (this is
          // exactly what surfaced the iPhone-vs-tablet mismatch in the first place), and it's free: this
          // tile is already live and already running its own render loop there either way.
          measurePerformance={isPrimary} onPerformanceMeasured={onPerformanceMeasured}
        />
      ) : (
        // Inactive mobile slide -- the plain photo only, no 3D overlay, until it's swiped to (see
        // mountLive above). Swapped for the real composited tile the moment it becomes active.
        // eslint-disable-next-line @next/next/no-img-element -- admin-supplied local path, not user content
        <img src={image.photoPath} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ))}
    </button>
  );
}

// Traditional carousel page-dots -- mobile-only (hidden via CSS, see globals.css), so on desktop this
// renders but stays invisible; simpler than a JS isMobile check here too, and matches how the gallery
// container's own grid<->carousel switch is already pure-CSS, not JS-branched. Hidden entirely (via count)
// for a single photo -- nothing to hint "swipe for more" about.
function CarouselDots({ count, activeIndex, onSelect }: { count: number; activeIndex: number; onSelect: (index: number) => void }) {
  if (count <= 1) return null;
  return (
    <div className="customize-preview-dots">
      {Array.from({ length: count }, (_, i) => (
        <button
          key={i}
          type="button"
          className={i === activeIndex ? "customize-preview-dot active" : "customize-preview-dot"}
          onClick={() => onSelect(i)}
          aria-label={`Show image ${i + 1} of ${count}`}
        />
      ))}
    </div>
  );
}

// An isometric cube inside four scan-frame corners -- reads as "3D / AR view" without a word, same
// convention as the AR badges on Amazon/Apple product pages. Icon-only (explicit request, to save
// space): the cube's own idle spin (.customize-preview-3d-cube, globals.css) is what signals "clickable"
// in place of a text label, while the frame corners stay still; the button itself still carries the real
// aria-label for screen readers.
function CubeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8 V5 a2 2 0 0 1 2 -2 H8" />
      <path d="M16 3 H19 a2 2 0 0 1 2 2 V8" />
      <path d="M21 16 V19 a2 2 0 0 1 -2 2 H16" />
      <path d="M8 21 H5 a2 2 0 0 1 -2 -2 V16" />
      <g className="customize-preview-3d-cube">
        <path d="M12 7 L16.3 9.5 L16.3 14.5 L12 17 L7.7 14.5 L7.7 9.5 Z" />
        <path d="M7.7 9.5 L12 12 L16.3 9.5" />
        <path d="M12 12 L12 17" />
      </g>
    </svg>
  );
}

// Two crossed strokes, not the text "×" character this used to render as -- a text glyph's true visual
// center depends on the font's own metrics/line-height, which rendered visibly off-center in testing;
// an SVG glyph is centered by the button's own flex layout instead (.customize-preview-lightbox-close,
// globals.css) and gives real control over stroke weight, used by both close buttons below.
function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6 L18 18" />
      <path d="M18 6 L6 18" />
    </svg>
  );
}

// Free-orbit 3D view, opened from the floating button below (mobile only). Shows the same live Model
// this file already renders for the photo composite, but standalone this time -- no photo, no fixed
// calibrated camera. Bounds auto-frames it (same pattern already validated in /dev/color-poc's own
// "Studio" mode) and OrbitControls lets the shopper actually turn it in their hands.
//
// Lighting reuses the mobile photo's own KeyLight + exposure/envIntensity/fillIntensity (explicit
// choice -- matches the still photos' mood) aimed at the origin, but deliberately NOT that photo's own
// Placement (position/rotation/scale). Placement encodes a tilt calibrated to line up with ONE fixed
// camera angle for the 2D composite -- reusing it here would hand the shopper an arbitrary-looking
// skewed starting pose the moment they can orbit freely. Model's own self-correcting "cap up"
// orientation (see basalt-mount-model.tsx) is the natural "as if held in your hand" rest pose instead.
function Preview3DLightbox({
  image, groupColors, onClose, modelPath,
}: { image: CalibrationImage; groupColors: Record<GroupKey, string>; onClose: () => void; modelPath: string }) {
  const [ready, setReady] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const glCanvasRef = useDisposeGLOnUnmount();

  // Auto-rotate is just a "yes, this turns" hint on open -- stops the instant the shopper actually drags
  // it (OrbitControls' own onStart), or after a few seconds regardless if they never touch it at all.
  useEffect(() => {
    const timer = window.setTimeout(() => setAutoRotate(false), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  // Hard fallback, same reasoning as PreviewTileContent's own fallback timer above -- Model's
  // onColorsApplied is the normal path to `ready`, but a real device's first WebGL frame/shader compile
  // can be slow (confirmed on a real iPhone for that tile's own canvas), and this mask has no retry loop
  // to fall back on if that callback is ever late or never fires. Without this, a stuck mask stays opaque
  // AND (default pointer-events) keeps intercepting clicks indefinitely -- which is worse here than on the
  // tile, since it sits directly over the close button.
  useEffect(() => {
    const timer = window.setTimeout(() => setReady(true), COMPOSITE_RETRY_MS + 500);
    return () => window.clearTimeout(timer);
  }, []);

  // Portaled straight to document.body -- this lightbox's parent chain includes .customize-preview
  // (position:sticky; z-index:10, see customize-experience.tsx), which creates ITS OWN stacking context.
  // A plain position:fixed;z-index:200 child of that gets trapped INSIDE that context for paint-order
  // purposes (fixed only escapes the LAYOUT/containing-block, not stacking) -- so its z-index:200 was only
  // ever competing against .customize-preview's own z-index:10, not the page's real stacking order, and
  // lost to the sticky site header (.buy-header, z-index:100) sitting outside it. Confirmed directly: the
  // close button was unclickable/invisible, hit-tested as "Back" underneath. Portaling out of that
  // ancestor entirely is the standard fix for this whole class of bug, not just a bump to this one number.
  return createPortal(
    <div className="customize-preview-lightbox customize-preview-3d-lightbox" onClick={onClose}>
      <div className="customize-preview-3d-media" onClick={(e) => e.stopPropagation()}>
        <Canvas gl={TRANSPARENT_CANVAS_GL} camera={{ fov: 35 }} ref={glCanvasRef}>
          <SceneLighting exposure={image.exposure} envIntensity={image.envIntensity} fillIntensity={image.fillIntensity} />
          <KeyLight config={image} targetPosition={[0, 0, 0]} />
          <Bounds fit clip observe margin={1.35}>
            <Model groupColors={groupColors} onColorsApplied={() => setReady(true)} modelPath={modelPath} />
          </Bounds>
          <OrbitControls
            makeDefault
            enablePan={false}
            autoRotate={autoRotate}
            autoRotateSpeed={2.2}
            onStart={() => setAutoRotate(false)}
          />
        </Canvas>
        <div className={`customize-preview-tile-loading${ready ? " is-ready" : ""}`} aria-hidden="true" />
      </div>
      <button
        type="button"
        className="customize-preview-lightbox-close customize-preview-3d-close"
        onClick={onClose}
        aria-label="Close 3D preview"
      >
        <CloseIcon />
      </button>
    </div>,
    document.body,
  );
}

export function CustomizePreviewGallery({
  images, groupColors, fallbackImageSrc, modelPath,
}: {
  images: CalibrationImage[];
  groupColors: Record<GroupKey, string>;
  fallbackImageSrc: string;
  // Full /models/... URL for the live 3D render (see basalt-mount-model.tsx's modelUrlForFile) --
  // sourced from the admin-configurable ThreeD:ActiveModelFile setting by the page that renders this.
  modelPath: string;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [show3D, setShow3D] = useState(false);
  // The REAL gate for whether more than the primary tile is allowed a live WebGL context -- a site-wide
  // singleton (lib/webgl-capability.ts), primed once as early as possible in the root layout
  // (WebglCapabilityPrimer), NOT recomputed here -- explicit request: the check should already be
  // resolved by the time a shopper reaches /customize, not run fresh on this page's own mount. Also
  // primed defensively right here too (idempotent, a harmless no-op if the layout's primer already ran)
  // so a client-side navigation straight to /customize before that primer's effect fires still resolves
  // this correctly rather than staying null forever. In a useEffect, not called directly in the render
  // body -- priming does real side-effecting work (creates a canvas, calls WebGL APIs, notifies
  // subscribers), which isn't safe to run during render itself.
  useEffect(() => {
    primeConcurrencyCapability();
  }, []);
  const concurrencyCapable = useConcurrencyCapable();
  // Comparison-only now, NOT wired into mountLive -- see DeviceCapabilityDebug, which shows this
  // alongside the maxTextureSize check and the static hardware-spec check so the tradeoff between them
  // stays visible on whatever device this runs on, not just reasoned about once and forgotten. Kept as
  // the full {fps, capable, decisionMs} result so the debug panel can show the real measured numbers.
  const [fpsMeasurement, setFpsMeasurement] = useState<{ fps: number; capable: boolean; decisionMs: number } | null>(null);
  const sorted = [...images].sort((a, b) => a.displayOrder - b.displayOrder);
  const expanded = sorted.find((i) => i.id === expandedId) ?? null;
  // Called unconditionally (Rules of Hooks) even though its result is only used in the images.length > 0
  // branch below -- passing "" is a harmless no-op (the effect just skips loading).
  const expandedAspect = useImageAspect(expanded?.photoPath ?? "");

  const galleryRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useIsMobile();
  // Mobile shows only ONE photo -- no carousel, no swipe, no dots -- per explicit request, rather than
  // continuing to chase carousel-specific glitches (edge-of-frame peeking on swipe, height mismatches)
  // that kept surfacing only on a real device and never in any emulator available here. Desktop is
  // unaffected either way, still the full flush collage of every active photo. With only one photo on
  // mobile, there's nothing to scroll between at all, so the whole class of carousel bugs stops applying
  // by construction, not by further patching.
  //
  // WHICH photo: the admin's own explicit pick (CalibrationImage.isMobileDisplay, set via the "Use for
  // mobile" checkbox on /admin/products/calibration's collage-crop preview), falling back to sorted[0]
  // (first by Display Order) when no image has that flag -- e.g. before an admin ever picks one, or if the
  // designated image was later deactivated/deleted. At most one image ever has the flag set (enforced
  // server-side, see AdminCalibrationImagesController's dedicated mobile-display endpoint), so .find here
  // never needs to pick among several.
  const mobileImage = sorted.find((i) => i.isMobileDisplay) ?? sorted[0];
  // Collapses to the SAME single-tile layout mobile uses whenever concurrencyCapable isn't confirmed
  // true -- not just mobile's own narrow viewport anymore. Explicit correction: this used to only gate
  // how many tiles got a LIVE WebGL context (still showing all 4 photo positions on a wide-but-weak
  // device, 3 of them static), which wasn't what was actually wanted -- an incapable device should get
  // mobile's whole simpler layout, not a 4-photo grid with most of it inert. `!== true` (not `=== false`)
  // is deliberate: while concurrencyCapable is still null (the one-time check hasn't resolved -- rare in
  // practice now that it's primed at site-load, see WebglCapabilityPrimer, but possible on a very fast
  // navigation), this defaults to the SAME conservative single-tile state rather than briefly showing the
  // full grid and then collapsing it a moment later once the real answer comes in.
  const singleTileLayout = isMobile || concurrencyCapable !== true;
  const visibleImages = singleTileLayout ? (mobileImage ? [mobileImage] : []) : sorted;

  // Only ever actually scrolls on the mobile carousel (the desktop grid isn't horizontally scrollable),
  // so this is a harmless no-op there -- scroll-snap guarantees each tile lands exactly on a multiple of
  // the container's own width, so rounding the current scroll position by that width gives the active
  // index directly, no separate IntersectionObserver needed just to answer "which tile is centered."
  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;
    const onScroll = () => {
      const index = el.clientWidth > 0 ? Math.round(el.scrollLeft / el.clientWidth) : 0;
      setActiveIndex(Math.min(visibleImages.length - 1, Math.max(0, index)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [visibleImages.length]);

  const scrollToIndex = (index: number) => {
    galleryRef.current?.scrollTo({ left: index * galleryRef.current.clientWidth, behavior: "smooth" });
  };

  // Debounced copy of activeIndex, used ONLY to decide which mobile carousel slide mounts a live WebGL
  // context (see PreviewTile's own mountLive comment for why) -- the dots below still track activeIndex
  // directly, so the highlighted dot keeps up with a fast swipe even though the heavy WebGL mount
  // deliberately lags behind it until the swipe actually settles.
  const [debouncedActiveIndex, setDebouncedActiveIndex] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedActiveIndex(activeIndex), 350);
    return () => clearTimeout(timer);
  }, [activeIndex]);

  if (images.length === 0) {
    // No calibration photos set up yet -- fall back to the original static placeholder rather than
    // showing an empty gallery.
    return (
      // eslint-disable-next-line @next/next/no-img-element -- static local asset, unchanged from before
      <img src={fallbackImageSrc} alt="Basalt mount preview" className="customize-preview-image" />
    );
  }

  return (
    <>
      <DeviceCapabilityDebug measuredFps={fpsMeasurement} />
      <div className="customize-preview-stage">
        <div
          ref={galleryRef}
          className={visibleImages.length === 1 ? "customize-preview-gallery single" : "customize-preview-gallery"}
          // Fixed square on mobile now (see squareGrid below), not the active photo's own aspect -- no need to
          // wait on an async image-aspect probe for a value that's now a constant.
          style={isMobile ? { height: 0, paddingBottom: "100%" } : undefined}
        >
          {visibleImages.map((image, index) => (
            <PreviewTile
              key={image.id} image={image} groupColors={groupColors} onExpand={() => setExpandedId(image.id)}
              // Mobile now always crops to the admin's saved square CollageCropConfig too (explicit request --
              // the admin curates a specific focal point/zoom for the mobile photo via the same "Collage crop"
              // tool used for the desktop grid, so mobile should actually use it instead of showing the
              // photo's full uncropped frame). Desktop is unchanged: square only once there's more than one
              // tile to align flush against. singleTileLayout (not just isMobile) so a wide-but-incapable
              // device's single tile ALSO gets the compact square crop, not the full-size real-aspect
              // treatment a genuinely-desktop single-photo product gets -- see PreviewTile's own comment.
              squareGrid={singleTileLayout || visibleImages.length > 1} isActive={index === debouncedActiveIndex}
              suspendLive={isMobile && show3D}
              isPrimary={index === 0} concurrencyCapable={concurrencyCapable} singleTileLayout={singleTileLayout}
              onPerformanceMeasured={index === 0 ? setFpsMeasurement : undefined} modelPath={modelPath}
            />
          ))}
        </div>
        {/* Mobile AND desktop -- one button for the whole viewport either way (see
            .customize-preview-3d-btn's own comment, globals.css, for why bottom-right here already means
            bottom-right of the full grid on desktop, not just the mobile single tile). Reuses
            `mobileImage` -- the admin's designated "mobile" photo, or sorted[0] -- as the one representative
            calibrated photo to light Preview3DLightbox from on EITHER platform; there's only ever one 3D
            button now, so there's no reason to pick a second, desktop-specific photo just for this.
            The "-grid" modifier (centered placement, see globals.css) only applies when there's an actual
            multi-tile grid to center over -- gated on !singleTileLayout, not raw viewport width, so a
            wide-but-capability-constrained device (which now renders exactly like mobile, see PreviewTile)
            also keeps the bottom-right corner placement instead of centering over its own single tile. */}
        {mobileImage && (
          <button
            type="button"
            className={`customize-preview-3d-btn${singleTileLayout ? "" : " customize-preview-3d-btn-grid"}`}
            onClick={() => setShow3D(true)}
            aria-label="Preview in 3D"
          >
            <CubeIcon />
          </button>
        )}
      </div>
      <CarouselDots count={visibleImages.length} activeIndex={activeIndex} onSelect={scrollToIndex} />

      {show3D && mobileImage && (
        <Preview3DLightbox image={mobileImage} groupColors={groupColors} onClose={() => setShow3D(false)} modelPath={modelPath} />
      )}

      {expanded && createPortal(
        // Portaled for the same reason as Preview3DLightbox above -- see its own comment. Desktop-only
        // today (this popup is disabled on mobile, see PreviewTile's onClick), but the stacking-context
        // trap is real on desktop too whenever this scrolls under a sticky header at the right moment, not
        // something specific to the 3D view.
        <div className="customize-preview-lightbox" onClick={() => setExpandedId(null)}>
          <div
            className="customize-preview-lightbox-media"
            style={expandedAspect ? { aspectRatio: `${expandedAspect}` } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {/* No download button -- right-click "Save image as" on this canvas saves the real blended
                result directly, since browsers save a canvas's actual pixel buffer, and that buffer is a
                copy of the grid tile's own (see LightboxMirror above). */}
            {expandedAspect && <LightboxMirror imageId={expanded.id} groupColors={groupColors} blend={expanded} />}
          </div>
          <button type="button" className="customize-preview-lightbox-close" onClick={() => setExpandedId(null)} aria-label="Close">
            <CloseIcon />
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}
